import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Package, Home, Upload, Download, Map, Settings, FileText, Sun, Moon, Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import LanguageSwitcher from './LanguageSwitcher';
import { getTranslation } from '../i18n/translations';

const Layout = ({ children }) => {
  const location = useLocation();
  const { whiteLabel, setWhiteLabel, logout, currentUser, language } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const primaryColor = whiteLabel ? '#2D4A8F' : '#F26724';
  const brandName = whiteLabel ? 'Smart Locker System' : 'Swedbank Lockers';
  
  const t = (key) => getTranslation(key, language);

  const navItems = [
    { path: '/', label: t('home'), icon: Home },
    { path: '/deposit', label: t('sendParcel'), icon: Upload },
    { path: '/retrieve', label: t('receiveParcel'), icon: Download },
    { path: '/lockers', label: t('lockerMap'), icon: Map },
    { path: '/mylog', label: t('myLog'), icon: FileText },
    { path: '/admin', label: t('admin'), icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F6F8]">
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Package size={32} style={{ color: primaryColor }} />
              <span className="text-xl font-semibold text-[#1A1A1A] hidden sm:inline">{brandName}</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant={isActive ? 'default' : 'ghost'}
                      size="sm"
                      className="transition-colors"
                      style={isActive ? { backgroundColor: primaryColor } : {}}
                    >
                      <Icon size={16} className="mr-2" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
            </div>

            {/* Right side controls */}
            <div className="flex items-center space-x-2">
              <LanguageSwitcher />
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setWhiteLabel(!whiteLabel)}
                className="transition-all hidden sm:flex"
              >
                {whiteLabel ? <Moon size={16} className="mr-2" /> : <Sun size={16} className="mr-2" />}
                <span className="hidden md:inline">{whiteLabel ? t('branded') : t('whiteLabel')}</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="transition-all hidden sm:flex"
              >
                <LogOut size={16} className="mr-2" />
                <span className="hidden md:inline">{t('logout')}</span>
              </Button>

              {/* Mobile menu button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden"
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-4 space-y-2">
              {navItems.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link 
                    key={item.path} 
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button
                      variant={isActive ? 'default' : 'ghost'}
                      size="sm"
                      className="w-full justify-start transition-colors"
                      style={isActive ? { backgroundColor: primaryColor } : {}}
                    >
                      <Icon size={16} className="mr-2" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setWhiteLabel(!whiteLabel);
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-start"
              >
                {whiteLabel ? <Moon size={16} className="mr-2" /> : <Sun size={16} className="mr-2" />}
                {whiteLabel ? t('branded') : t('whiteLabel')}
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="w-full justify-start"
              >
                <LogOut size={16} className="mr-2" />
                {t('logout')}
              </Button>
              
              {currentUser && (
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">{t('currentUser')}</p>
                  <p className="text-sm font-semibold text-[#1A1A1A]">{currentUser.name}</p>
                  <p className="text-xs text-gray-500">{currentUser.email}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
            <div className="text-sm text-gray-600">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs font-medium">
                {t('demoOnly')}
              </span>
            </div>
            <p className="text-sm text-gray-500 text-center">
              © 2025 {brandName}. {t('interactiveSimulation')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
